import { useState, useEffect, useMemo } from 'react';
import {
  getCustomerFeedbackDashboard, getClients, getUsers, getBusinessUnits
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import {
  Loader2, TrendingUp, TrendingDown, AlertTriangle, Users, Building2,
  BarChart3, Activity, Target, Minus, ThumbsUp, ThumbsDown, Package,
  ChevronsUpDown, Check
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import {
  format, subMonths, subYears, subQuarters,
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  startOfYear, endOfYear
} from 'date-fns';

const PERIOD_PRESETS = [
  { value: 'current_month', label: 'Mese corrente', getRange: () => ({ start: format(startOfMonth(new Date()), 'yyyy-MM-dd'), end: format(endOfMonth(new Date()), 'yyyy-MM-dd') }) },
  { value: 'last_month', label: 'Mese scorso', getRange: () => ({ start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'), end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd') }) },
  { value: 'current_quarter', label: 'Trimestre corrente', getRange: () => ({ start: format(startOfQuarter(new Date()), 'yyyy-MM-dd'), end: format(endOfQuarter(new Date()), 'yyyy-MM-dd') }) },
  { value: 'last_quarter', label: 'Trimestre scorso', getRange: () => ({ start: format(startOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd'), end: format(endOfQuarter(subQuarters(new Date(), 1)), 'yyyy-MM-dd') }) },
  { value: 'current_year', label: 'Anno corrente', getRange: () => ({ start: format(startOfYear(new Date()), 'yyyy-MM-dd'), end: format(endOfYear(new Date()), 'yyyy-MM-dd') }) },
  { value: 'last_year', label: 'Anno scorso', getRange: () => ({ start: format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd'), end: format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd') }) },
  { value: 'last_6_months', label: 'Ultimi 6 mesi', getRange: () => ({ start: format(subMonths(new Date(), 6), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { value: 'last_12_months', label: 'Ultimi 12 mesi', getRange: () => ({ start: format(subMonths(new Date(), 12), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { value: 'custom', label: 'Personalizzato' },
];

const AREAS = ['processi', 'operativi', 'pm', 'risultati'];
const AREA_LABELS = { processi: 'Processi', operativi: 'Operativi', pm: 'PM', risultati: 'Risultati' };
const AREA_COLORS = { processi: '#6366f1', operativi: '#f59e0b', pm: '#06b6d4', risultati: '#10b981' };

function getScoreColor(val) {
  if (val === null || val === undefined) return 'text-gray-400';
  if (val >= 0.5) return 'text-emerald-600';
  if (val >= -0.2) return 'text-amber-500';
  return 'text-red-500';
}

function getScoreBg(val) {
  if (val === null || val === undefined) return 'bg-gray-50';
  if (val >= 0.5) return 'bg-emerald-50';
  if (val >= -0.2) return 'bg-amber-50';
  return 'bg-red-50';
}

function ScoreCell({ value }) {
  if (value === null || value === undefined) return <span className="text-gray-300">-</span>;
  const color = getScoreColor(value);
  return <span className={`font-semibold ${color}`}>{value > 0 ? '+' : ''}{value.toFixed(2)}</span>;
}

function getNPSColor(score) {
  if (!score && score !== 0) return 'text-gray-400';
  if (score >= 9) return 'text-emerald-600';
  if (score >= 7) return 'text-amber-500';
  return 'text-red-500';
}

export default function FeedbackDashboard() {
  const { user } = useAuth();
  const defaultPreset = 'last_12_months';
  const defaultRange = PERIOD_PRESETS.find(p => p.value === defaultPreset).getRange();
  const [periodPreset, setPeriodPreset] = useState(defaultPreset);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [clientId, setClientId] = useState('');
  const [buId, setBuId] = useState('');
  const [pmId, setPmId] = useState('');
  const [accountManagerId, setAccountManagerId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [bus, setBus] = useState([]);

  const handlePresetChange = (value) => {
    setPeriodPreset(value);
    if (value !== 'custom') {
      const preset = PERIOD_PRESETS.find(p => p.value === value);
      if (preset?.getRange) {
        const range = preset.getRange();
        setStartDate(range.start);
        setEndDate(range.end);
      }
    }
  };

  const handleCustomDateChange = (type, value) => {
    setPeriodPreset('custom');
    if (type === 'start') setStartDate(value);
    else setEndDate(value);
  };

  useEffect(() => {
    Promise.all([
      getClients().catch(() => ({ data: [] })),
      getUsers(null, true).catch(() => ({ data: [] })),
      getBusinessUnits().catch(() => ({ data: [] })),
    ]).then(([c, u, b]) => {
      setClients(Array.isArray(c.data) ? c.data : []);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setBus(Array.isArray(b.data) ? b.data : []);
    });
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [startDate, endDate, clientId, buId, pmId, accountManagerId]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (clientId) params.client_id = clientId;
      if (buId) params.business_unit_id = buId;
      if (pmId) params.pm_id = pmId;
      if (accountManagerId) params.account_manager_id = accountManagerId;
      const res = await getCustomerFeedbackDashboard(params);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const pms = useMemo(() => users.filter(u => {
    const roles = (u.roles && u.roles.length > 0) ? u.roles : (u.role ? [u.role] : []);
    return roles.includes('project_manager') || roles.includes('supervisor');
  }), [users]);

  const accounts = useMemo(() => users.filter(u => {
    const roles = (u.roles && u.roles.length > 0) ? u.roles : (u.role ? [u.role] : []);
    return roles.includes('account') || roles.includes('admin');
  }), [users]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const s = data?.summary || {};
  const trends = data?.trends || {};
  const relTrend = data?.relationship_trend || {};
  const byBu = data?.by_business_unit || [];
  const byPm = data?.by_pm || [];
  const byProduct = data?.by_product || [];
  const alerts = data?.alerts || {};
  const dist = data?.distribution || {};

  const npsDelta = s.avg_nps !== null && s.nps_prev !== null ? +(s.avg_nps - s.nps_prev).toFixed(1) : null;

  return (
    <div className="space-y-6">
      {/* FILTERS */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 min-w-[160px]">
              <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Periodo *</Label>
              <Select value={periodPreset} onValueChange={handlePresetChange}>
                <SelectTrigger className="h-8 text-xs" data-testid="dash-period-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_PRESETS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {periodPreset === 'custom' && (
              <div className="flex items-center gap-1">
                <Input type="date" value={startDate} onChange={e => handleCustomDateChange('start', e.target.value)}
                  className="h-8 text-xs w-[130px]" data-testid="dash-start-date" />
                <span className="text-gray-300 text-xs">-</span>
                <Input type="date" value={endDate} onChange={e => handleCustomDateChange('end', e.target.value)}
                  className="h-8 text-xs w-[130px]" data-testid="dash-end-date" />
              </div>
            )}
            <SearchableFilterSelect label="Cliente" value={clientId} onChange={setClientId}
              options={clients.map(c => ({ value: c.id, label: c.company_name }))} testId="dash-filter-client" />
            <FilterSelect label="Business Unit" value={buId} onChange={setBuId}
              options={bus.map(b => ({ value: b.id, label: b.name }))} testId="dash-filter-bu" />
            <SearchableFilterSelect label="Project Manager" value={pmId} onChange={setPmId}
              options={pms.map(u => ({ value: u.id, label: u.name }))} testId="dash-filter-pm" />
            <SearchableFilterSelect label="Account Manager" value={accountManagerId} onChange={setAccountManagerId}
              options={accounts.map(u => ({ value: u.id, label: u.name }))} testId="dash-filter-account" />
            {(clientId || buId || pmId || accountManagerId) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500"
                onClick={() => { setClientId(''); setBuId(''); setPmId(''); setAccountManagerId(''); }}
                data-testid="dash-clear-filters">
                Reset filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {s.total_feedbacks === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-600 mb-1">Nessun feedback nel periodo selezionato</h3>
            <p className="text-sm text-gray-400">Modifica i filtri o il periodo per visualizzare i dati</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* SECTION 1: KPI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* NPS medio */}
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">NPS Medio</span>
                  <Target className="w-4 h-4 text-orange-400" />
                </div>
                <div className="flex items-end gap-2">
                  <span className={`text-3xl font-bold ${getNPSColor(s.avg_nps)}`} data-testid="kpi-avg-nps">
                    {s.avg_nps ?? '-'}
                  </span>
                  {npsDelta !== null && (
                    <span className={`text-xs font-medium mb-1 flex items-center gap-0.5 ${npsDelta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {npsDelta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {npsDelta > 0 ? '+' : ''}{npsDelta}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">{s.total_feedbacks} feedback totali</span>
              </CardContent>
            </Card>

            {/* NPS Distribution */}
            <Card>
              <CardContent className="py-4 px-5">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Distribuzione NPS</span>
                <div className="mt-3 space-y-2">
                  <NPSBar label="Promotori (9-10)" pct={s.nps_distribution?.promoters_pct} count={s.nps_distribution?.promoters} color="bg-emerald-500" />
                  <NPSBar label="Passivi (7-8)" pct={s.nps_distribution?.passives_pct} count={s.nps_distribution?.passives} color="bg-amber-400" />
                  <NPSBar label="Detrattori (1-6)" pct={s.nps_distribution?.detractors_pct} count={s.nps_distribution?.detractors} color="bg-red-500" />
                </div>
              </CardContent>
            </Card>

            {/* Area Averages */}
            <Card>
              <CardContent className="py-4 px-5">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Media Giudizi per Area</span>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {AREAS.map(a => (
                    <div key={a} className={`rounded-lg px-3 py-2 ${getScoreBg(s.area_averages?.[a])}`}>
                      <div className="text-[10px] text-gray-500 uppercase">{AREA_LABELS[a]}</div>
                      <div className={`text-lg font-bold ${getScoreColor(s.area_averages?.[a])}`}>
                        {s.area_averages?.[a] !== null && s.area_averages?.[a] !== undefined
                          ? (s.area_averages[a] > 0 ? '+' : '') + s.area_averages[a].toFixed(2)
                          : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* SECTION 2: TRENDS */}
          {(trends.nps?.length > 1 || trends.areas?.length > 1) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {trends.nps?.length > 1 && (
                <Card>
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-semibold text-gray-700">Trend NPS</CardTitle>
                  </CardHeader>
                  <CardContent className="px-2 pb-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={trends.nps}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="avg_nps" name="NPS" stroke="#f97316"
                          strokeWidth={2.5} dot={{ r: 4, fill: '#f97316' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {trends.areas?.length > 1 && (
                <Card>
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-semibold text-gray-700">Trend Giudizi per Area</CardTitle>
                  </CardHeader>
                  <CardContent className="px-2 pb-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={trends.areas}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                        <YAxis domain={[-1, 1]} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                        {AREAS.map(a => (
                          <Line key={a} type="monotone" dataKey={a} name={AREA_LABELS[a]}
                            stroke={AREA_COLORS[a]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* SECTION 2B: RELATIONSHIP TREND (by contract month) */}
          {(relTrend.nps?.length > 0 || relTrend.areas?.length > 0) && (
            <Card>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-500" /> Evoluzione per Mese di Relazione
                </CardTitle>
                <p className="text-[11px] text-gray-400 mt-0.5">Soddisfazione in base alla fase della relazione contrattuale (mese 1 = primo mese dal contratto)</p>
              </CardHeader>
              <CardContent className="px-2 pb-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {relTrend.nps?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 px-3">NPS per Mese di Relazione</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={relTrend.nps} barSize={32}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9ca3af"
                            tickFormatter={v => `Mese ${v}`} />
                          <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                          <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                            labelFormatter={v => `Mese ${v}`}
                            formatter={(val, name) => [val, name === 'avg_nps' ? 'NPS' : name]}
                          />
                          <Bar dataKey="avg_nps" name="NPS" radius={[4, 4, 0, 0]}>
                            {relTrend.nps.map((entry, i) => (
                              <Cell key={i} fill={
                                entry.avg_nps >= 9 ? '#10b981' :
                                entry.avg_nps >= 7 ? '#f59e0b' : '#ef4444'
                              } />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {relTrend.areas?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 px-3">Giudizi per Mese di Relazione</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={relTrend.areas}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9ca3af"
                            tickFormatter={v => `Mese ${v}`} />
                          <YAxis domain={[-1, 1]} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                          <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                            labelFormatter={v => `Mese ${v}`}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                          {AREAS.map(a => (
                            <Line key={a} type="monotone" dataKey={a} name={AREA_LABELS[a]}
                              stroke={AREA_COLORS[a]} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* SECTION 3: BY BUSINESS UNIT */}
          {byBu.length > 0 && (
            <Card>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-orange-500" /> Analisi per Business Unit
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="bu-analysis-table">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-2">Business Unit</th>
                        <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">NPS</th>
                        {AREAS.map(a => (
                          <th key={a} className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">{AREA_LABELS[a]}</th>
                        ))}
                        <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2"># Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byBu.map((row, i) => (
                        <tr key={row.business_unit_id} className={`border-b border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                          <td className="px-5 py-2.5">
                            <span className="text-sm font-medium text-gray-800">{row.business_unit_name}</span>
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <span className={`text-sm font-bold ${getNPSColor(row.avg_nps)}`}>{row.avg_nps ?? '-'}</span>
                          </td>
                          {AREAS.map(a => (
                            <td key={a} className="text-center px-3 py-2.5"><ScoreCell value={row[a]} /></td>
                          ))}
                          <td className="text-center px-3 py-2.5">
                            <Badge variant="secondary" className="text-xs">{row.feedback_count}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SECTION 4: BY PM + BY PRODUCT */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-500" /> Analisi per Project Manager
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                {byPm.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="pm-analysis-table">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-2">PM</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">NPS</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">PM Score</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">Risultati</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byPm.map((row, i) => (
                          <tr key={row.pm_id} className={`border-b border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                            <td className="px-5 py-2.5 text-sm font-medium text-gray-800">{row.pm_name}</td>
                            <td className="text-center px-3 py-2.5">
                              <span className={`text-sm font-bold ${getNPSColor(row.avg_nps)}`}>{row.avg_nps ?? '-'}</span>
                            </td>
                            <td className="text-center px-3 py-2.5"><ScoreCell value={row.pm_score} /></td>
                            <td className="text-center px-3 py-2.5"><ScoreCell value={row.risultati} /></td>
                            <td className="text-center px-3 py-2.5">
                              <Badge variant="secondary" className="text-xs">{row.feedback_count}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 px-5 py-6">Nessun PM associato alle BU nei feedback. Assegna i PM ai progetti per popolare questa tabella.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Package className="w-4 h-4 text-indigo-500" /> Analisi per Prodotto
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                {byProduct.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="product-analysis-table">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-2">Prodotto</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">NPS</th>
                          {AREAS.map(a => (
                            <th key={a} className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">{AREA_LABELS[a]}</th>
                          ))}
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byProduct.map((row, i) => (
                          <tr key={row.product_id} className={`border-b border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                            <td className="px-5 py-2.5 text-sm font-medium text-gray-800">{row.product_name}</td>
                            <td className="text-center px-3 py-2.5">
                              <span className={`text-sm font-bold ${getNPSColor(row.avg_nps)}`}>{row.avg_nps ?? '-'}</span>
                            </td>
                            {AREAS.map(a => (
                              <td key={a} className="text-center px-3 py-2.5"><ScoreCell value={row[a]} /></td>
                            ))}
                            <td className="text-center px-3 py-2.5">
                              <Badge variant="secondary" className="text-xs">{row.feedback_count}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 px-5 py-6">Nessun prodotto associato alle BU nei feedback.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* SECTION 5: ALERTS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* At-risk clients */}
            <Card className="border-l-4 border-l-red-400">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" /> Clienti a Rischio
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                {(alerts.at_risk_clients || []).length === 0 ? (
                  <p className="text-sm text-gray-400 px-5 py-4">Nessun cliente a rischio</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="at-risk-table">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-5 py-2">Cliente</th>
                          <th className="text-center text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">NPS</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">Area Critica</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">BU</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.at_risk_clients.map((c, i) => (
                          <tr key={c.client_id} className="border-b border-gray-50">
                            <td className="px-5 py-2 text-sm font-medium text-gray-800">{c.client_name}</td>
                            <td className="text-center px-3 py-2">
                              <span className={`text-sm font-bold ${getNPSColor(c.nps)}`}>{c.nps ?? '-'}</span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {c.critical_areas.map(a => (
                                  <Badge key={a} variant="destructive" className="text-[10px]">{AREA_LABELS[a] || a}</Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">{c.business_units.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent negatives */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <ThumbsDown className="w-4 h-4 text-red-400" /> Feedback Negativi Recenti
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-2">
                {(alerts.recent_negatives || []).length === 0 ? (
                  <p className="text-sm text-gray-400 px-5 py-4">Nessun feedback negativo</p>
                ) : (
                  <div className="max-h-[280px] overflow-y-auto">
                    <table className="w-full" data-testid="recent-negatives-table">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-5 py-2">Cliente</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">BU</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">Area</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">Note</th>
                          <th className="text-left text-[10px] font-semibold text-gray-400 uppercase px-3 py-2">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.recent_negatives.map((n, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-5 py-2 text-xs font-medium text-gray-800">{n.client_name}</td>
                            <td className="px-3 py-2 text-xs text-gray-600">{n.business_unit_name}</td>
                            <td className="px-3 py-2">
                              <Badge variant="destructive" className="text-[10px]">{AREA_LABELS[n.area] || n.area}</Badge>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate">{n.notes || '-'}</td>
                            <td className="px-3 py-2 text-xs text-gray-400">{n.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* SECTION 6: DISTRIBUTION */}
          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-500" /> Distribuzione Giudizi per Area
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {AREAS.map(a => {
                  const d = dist[a] || { positivo: 0, neutro: 0, negativo: 0 };
                  const total = d.positivo + d.neutro + d.negativo || 1;
                  const pctPos = Math.round(d.positivo / total * 100);
                  const pctNeu = Math.round(d.neutro / total * 100);
                  const pctNeg = Math.round(d.negativo / total * 100);
                  return (
                    <div key={a} className="space-y-2">
                      <div className="text-xs font-semibold text-gray-600 uppercase">{AREA_LABELS[a]}</div>
                      <DistBar label="Positivo" pct={pctPos} count={d.positivo} color="bg-emerald-500" />
                      <DistBar label="Neutro" pct={pctNeu} count={d.neutro} color="bg-amber-400" />
                      <DistBar label="Negativo" pct={pctNeg} count={d.negativo} color="bg-red-500" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1 min-w-[140px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Select value={value || 'all'} onValueChange={v => onChange(v === 'all' ? '' : v)}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder="Tutti" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SearchableFilterSelect({ label, value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="space-y-1 min-w-[140px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-8 text-xs w-full justify-between font-normal px-3" data-testid={testId}>
            <span className="truncate">{selected ? selected.label : 'Tutti'}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca..." className="h-8 text-xs" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>Nessun risultato</CommandEmpty>
              <CommandGroup>
                <CommandItem value="__all__" onSelect={() => { onChange(''); setOpen(false); }} data-testid={`${testId}-all`}>
                  <Check className={`mr-2 h-3 w-3 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                  Tutti
                </CommandItem>
                {options.map(o => (
                  <CommandItem key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }} data-testid={`${testId}-opt-${o.value}`}>
                    <Check className={`mr-2 h-3 w-3 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function NPSBar({ label, pct, count, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-gray-500 w-[90px] shrink-0">{label}</span>
      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct || 0}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-gray-600 w-10 text-right">{pct || 0}%</span>
      <span className="text-[10px] text-gray-400 w-4 text-right">{count}</span>
    </div>
  );
}

function DistBar({ label, pct, count, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-gray-500 w-[55px] shrink-0">{label}</span>
      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-gray-600 w-8 text-right">{pct}%</span>
    </div>
  );
}
